import React from 'react';

/**
 * Circuit diagram renderer using custom SVG
 * Renders basic electronic components and wires
 */
export function CircuitRenderer({ data }) {
  const { components = [], wires = [] } = data;

  // Component symbol renderers
  const renderComponent = (comp, index) => {
    const { type, x, y, voltage, value } = comp;

    switch (type) {
      case 'battery':
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            {/* Battery symbol - two parallel lines of different lengths */}
            <line x1="-5" y1="-15" x2="-5" y2="15" stroke="black" strokeWidth="3" />
            <line x1="5" y1="-10" x2="5" y2="10" stroke="black" strokeWidth="3" />
            <text x="0" y="-25" textAnchor="middle" fontSize="12" fill="currentColor">
              {voltage || 'V'}
            </text>
          </g>
        );

      case 'resistor':
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            {/* Resistor symbol - zigzag line */}
            <path
              d="M-20,0 L-15,-8 L-10,8 L-5,-8 L0,8 L5,-8 L10,8 L15,-8 L20,0"
              fill="none"
              stroke="black"
              strokeWidth="2"
            />
            <text x="0" y="-15" textAnchor="middle" fontSize="12" fill="currentColor">
              {value || 'R'}
            </text>
          </g>
        );

      case 'led':
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            {/* LED symbol - triangle with line */}
            <path
              d="M-10,10 L10,0 L-10,-10 Z"
              fill="none"
              stroke="black"
              strokeWidth="2"
            />
            <line x1="10" y1="-10" x2="10" y2="10" stroke="black" strokeWidth="2" />
            {/* Light rays */}
            <path
              d="M15,-8 L20,-12 M15,-4 L20,-6"
              stroke="#ef4444"
              strokeWidth="1.5"
            />
            <text x="0" y="-20" textAnchor="middle" fontSize="12" fill="currentColor">
              LED
            </text>
          </g>
        );

      case 'capacitor':
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            {/* Capacitor symbol - two parallel lines */}
            <line x1="-2" y1="-15" x2="-2" y2="15" stroke="black" strokeWidth="2" />
            <line x1="2" y1="-15" x2="2" y2="15" stroke="black" strokeWidth="2" />
            <text x="0" y="-25" textAnchor="middle" fontSize="12" fill="currentColor">
              {value || 'C'}
            </text>
          </g>
        );

      case 'ground':
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            {/* Ground symbol */}
            <line x1="0" y1="0" x2="0" y2="10" stroke="black" strokeWidth="2" />
            <line x1="-15" y1="10" x2="15" y2="10" stroke="black" strokeWidth="2" />
            <line x1="-10" y1="15" x2="10" y2="15" stroke="black" strokeWidth="2" />
            <line x1="-5" y1="20" x2="5" y2="20" stroke="black" strokeWidth="2" />
          </g>
        );

      case 'switch':
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            {/* Switch symbol */}
            <circle cx="-10" cy="0" r="2" fill="black" />
            <circle cx="10" cy="0" r="2" fill="black" />
            <line x1="-8" y1="0" x2="5" y2="-8" stroke="black" strokeWidth="2" />
            <text x="0" y="-20" textAnchor="middle" fontSize="12" fill="currentColor">
              SW
            </text>
          </g>
        );

      default:
        return (
          <g key={index} transform={`translate(${x}, ${y})`}>
            <circle r="8" fill="gray" />
            <text x="0" y="25" textAnchor="middle" fontSize="10" fill="currentColor">
              {type}
            </text>
          </g>
        );
    }
  };

  return (
    <div className="circuit-container w-full max-w-3xl mx-auto">
      <svg
        viewBox="0 0 500 300"
        className="w-full h-auto border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900"
        style={{ minHeight: '200px' }}
      >
        {/* Render wires first (behind components) */}
        {wires.map((wire, index) => (
          <line
            key={index}
            x1={wire.x1}
            y1={wire.y1}
            x2={wire.x2}
            y2={wire.y2}
            stroke="black"
            strokeWidth="2"
          />
        ))}

        {/* Render components */}
        {components.map((comp, index) => renderComponent(comp, index))}
      </svg>

      {components.length > 0 && (
        <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          <strong>Components:</strong>{' '}
          {components
            .map((c) => {
              if (c.value) return `${c.type} (${c.value})`;
              if (c.voltage) return `${c.type} (${c.voltage})`;
              return c.type;
            })
            .join(', ')}
        </div>
      )}
    </div>
  );
}
